/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { SelectChangeEvent } from '@mui/material';
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { clamp, cloneDeep, isEqual } from 'lodash';
import React from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  ReactHookFormProvider,
  defaultFormOptions,
} from '@opengeoweb/form-fields';
import type { DrawMode } from '@opengeoweb/webmap-react';
import { getGeoJSONPropertyValue } from '@opengeoweb/webmap-react';
import type { ComparableDrawing } from './drawingObjectUtils';
import {
  createDefaultObjectNameGenerator,
  getComparableDrawing,
  getDrawingObjects,
  getDrawingToSubmit,
} from './drawingObjectUtils';
import type { DrawingToolFormValues } from './DrawingToolFormContents';
import { DrawingToolFormContents } from './DrawingToolFormContents';
import type { DrawingObjectActions } from './DrawingToolFormObjects';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import type { WarningType } from '../../store/warningsForm/types';
import { useFormDirty } from '../../utils/useFormDirty';
import { getUtcDateLabel } from '../../utils/warningUtils';

export const opacityOptions = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];
const emptyMasks: DrawingListItem[] = [];
const noop = (): void => {};
const noopObjectActions: DrawingObjectActions = {
  onSelect: noop,
  onDeselect: noop,
  onHover: noop,
  onRename: noop,
  onDelete: noop,
  onToggleVisibility: noop,
  onReorder: noop,
};
const emptyFeatureIds: string[] = [];
export const getDrawingName = (drawingName: string): string =>
  drawingName || getUtcDateLabel();

export interface DrawingToolFormComponentProps {
  onSubmitForm: (formValues: DrawingToolFormValues) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  drawingName?: string;
  isLoading?: boolean;
  isLoadingSavedDrawing?: boolean;
  dialogError?: string;
  id?: string;
  drawModes: DrawMode[];
  onChangeDrawMode: (newMode: DrawMode) => void;
  activeDrawModeId: string;
  onDeactivateTool: () => void;
  isInEditMode: boolean;
  geoJSONProperties: GeoJSON.GeoJsonProperties;
  onChangeProperties: (properties: GeoJSON.GeoJsonProperties) => void;
  onChangeName: (name: string) => void;
  objectActions?: DrawingObjectActions;
  onFormDirty?: (isFormDirty: boolean) => void;
  geoJSON: FeatureCollection<Geometry, GeoJsonProperties> | undefined;
  savedDrawing?: ComparableDrawing; // Current drawing from BE (undefined if new drawing)
  lastUpdatedTime?: string;
  selectedMask?: FeatureCollection<Geometry, GeoJsonProperties>;
  onClickShare?: (type: WarningType) => void;
  masks?: DrawingListItem[];
  isLoadingMasks?: boolean;
  maskError?: string;
  selectedMaskId?: string;
  onSelectMask?: (maskId?: string) => void;
  onHoverMask?: (maskId?: string) => void;
  selectedFeatureIds?: string[];
}

const DrawingToolForm: React.FC<DrawingToolFormComponentProps> = ({
  onSubmitForm,
  isLoading = false,
  isLoadingSavedDrawing = false,
  dialogError,
  id = '',
  drawModes = [],
  onChangeDrawMode = (): void => {},
  activeDrawModeId = '',
  onDeactivateTool = (): void => {},
  isInEditMode = false,
  geoJSONProperties = {},
  onChangeProperties = (): void => {},
  onChangeName = (): void => {},
  objectActions = noopObjectActions,
  onFormDirty = (): void => {},
  geoJSON,
  savedDrawing,
  lastUpdatedTime,
  selectedMask,
  onClickShare = (): void => {},
  masks = emptyMasks,
  isLoadingMasks = false,
  maskError,
  selectedMaskId,
  onSelectMask = noop,
  onHoverMask = noop,
  selectedFeatureIds = emptyFeatureIds,
}: DrawingToolFormComponentProps) => {
  const {
    handleSubmit,
    setValue,

    formState: { errors, isDirty: hasBeenModified },
    getValues,
    getFieldState,
    trigger,
    control,
  } = useFormContext();

  const { fields, replace } = useFieldArray<DrawingToolFormValues, 'features'>({
    control: control as unknown as Control<DrawingToolFormValues>,
    name: 'features',
  });

  // copy errors because react-hook-form mutates the drawing which breaks memoization
  const currentErrors = cloneDeep(errors);

  const generatorRef = React.useRef<
    ReturnType<typeof createDefaultObjectNameGenerator> | undefined
  >(undefined);
  generatorRef.current ??= createDefaultObjectNameGenerator();
  const getDefaultObjectName = generatorRef.current;

  const drawingObjects = React.useMemo(
    () => getDrawingObjects(geoJSON, getDefaultObjectName),
    [geoJSON, getDefaultObjectName],
  );

  const [watchedObjectName, watchedFeatures] = useWatch({
    control: control as unknown as Control<DrawingToolFormValues>,
    name: ['objectName', 'features'],
  });
  // Construct reference so we can compare against what is currently stored in the BE
  const currentDrawing = React.useMemo(() => {
    const drawingToSubmit = getDrawingToSubmit({
      objectName: watchedObjectName,
      features: watchedFeatures,
      geoJSON,
    });
    return getComparableDrawing({
      ...drawingToSubmit,
      ...(selectedMask && {
        transformation: {
          mask: selectedMask,
          original: drawingToSubmit.geoJSON,
        },
      }),
    });
  }, [watchedObjectName, watchedFeatures, geoJSON, selectedMask]);
  // for a new drawing nothing is stored yet, so the form as it was opened is
  const initialDrawingRef = React.useRef(currentDrawing);
  const storedDrawing = id ? savedDrawing : initialDrawingRef.current;

  const isDirty = React.useMemo(
    () => (storedDrawing ? !isEqual(currentDrawing, storedDrawing) : true),
    [currentDrawing, storedDrawing],
  );

  const isShareable = !!id && !!savedDrawing && !isDirty;

  useFormDirty({ isDirty, onFormDirty });

  // keep the object form fields in sync with the features of the drawing
  React.useEffect(() => {
    const currentFormObjects = (getValues('features') ??
      []) as DrawingToolFormValues['features'];
    const newFormObjects = drawingObjects.map(({ featureId, objectName }) => ({
      featureId,
      objectName:
        currentFormObjects.find(
          (formObject) => formObject.featureId === featureId,
        )?.objectName ?? objectName,
    }));

    if (!isEqual(currentFormObjects, newFormObjects)) {
      replace(newFormObjects);
      // replace keeps field errors at their old index, so revalidate them
      if (getFieldState('features').invalid) {
        void trigger('features');
      }
    }
  }, [drawingObjects, getValues, getFieldState, trigger, replace]);

  React.useEffect(() => {
    if (activeDrawModeId || activeDrawModeId === '') {
      setValue('geoJSON', geoJSON, {
        shouldDirty: true,
      });
    }
  }, [activeDrawModeId, geoJSON, setValue]);

  React.useEffect(() => {
    // Only set form value if not in edit mode
    if (!isInEditMode && !isLoading && geoJSON) {
      setValue('geoJSON', geoJSON, {
        shouldValidate: true,
      });
    }
  }, [isLoading, isInEditMode, geoJSON, setValue]);

  const onSubmit = React.useCallback(
    (formValues: FieldValues) => {
      onDeactivateTool();
      onSubmitForm({
        ...getDrawingToSubmit(formValues),
        id,
      } as DrawingToolFormValues);
    },
    [id, onDeactivateTool, onSubmitForm],
  );

  const opacity = React.useMemo(
    () =>
      getGeoJSONPropertyValue(
        'fill-opacity',
        geoJSONProperties,
        drawModes.find((mode) => mode.value === 'POLYGON'),
      ) as number,
    [geoJSONProperties, drawModes],
  );

  const onChangeOpacity = React.useCallback(
    (event: SelectChangeEvent<unknown>): void => {
      const target = event.target as HTMLInputElement;
      const { value } = target;
      const newOpacity = parseInt(value, 10) / 100;
      const additionalStrokeOpacity = 0.8;
      onChangeProperties({
        'fill-opacity': newOpacity,
        'stroke-opacity': clamp(newOpacity + additionalStrokeOpacity, 0.01, 1),
      });
    },
    [onChangeProperties],
  );

  const onBlurName = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      onChangeName(event.target.value);
    },
    [onChangeName],
  );

  return (
    <DrawingToolFormContents
      activeDrawModeId={activeDrawModeId}
      dialogError={dialogError}
      drawModes={drawModes}
      drawingObjects={drawingObjects}
      errors={currentErrors}
      fields={fields}
      geoJSONProperties={geoJSONProperties}
      hasBeenModified={hasBeenModified}
      id={id}
      isInEditMode={isInEditMode}
      isLoading={isLoading}
      isLoadingMasks={isLoadingMasks}
      isLoadingSavedDrawing={isLoadingSavedDrawing}
      isShareable={isShareable}
      lastUpdatedTime={lastUpdatedTime}
      maskError={maskError}
      masks={masks}
      objectActions={objectActions}
      onBlurName={onBlurName}
      onChangeDrawMode={onChangeDrawMode}
      onChangeOpacity={onChangeOpacity}
      onClickShare={onClickShare}
      onHoverMask={onHoverMask}
      onSelectMask={onSelectMask}
      onSubmit={handleSubmit(onSubmit)}
      opacity={opacity}
      selectedFeatureIds={selectedFeatureIds}
      selectedMaskId={selectedMaskId}
    />
  );
};

const Wrapper: React.FC<DrawingToolFormComponentProps> = (
  props: DrawingToolFormComponentProps,
) => {
  const { id = '', drawingName = '', geoJSON = undefined } = props;
  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
        defaultValues: {
          id,
          objectName: getDrawingName(drawingName),
          features: [],
          geoJSON,
          opacity: 20,
        },
      }}
    >
      <DrawingToolForm {...props} />
    </ReactHookFormProvider>
  );
};

export { Wrapper as DrawingToolForm };

export default Wrapper;
